import React, { useState } from "react";
import {
    Grow,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
    MenuList,
    CircularProgress
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
    Edit as EditIcon,
    Undo as ReplyIcon,
    FastForward as ReAssignIcon,
    Redo as ForwardIcon,
    WorkOutline,
    AccessTime
} from "@mui/icons-material";
import caseActivityTypes from "../constants/caseActivityTypes";
import caseService from "../services/caseService";
import { useDispatch, useSelector } from "react-redux";
import {
    setCurrentMiscActivityOpen,
    setExistingMiscActivityFiles,
    setInternalNoteDescription,
    setMeetingCardMeeting,
    setMeetingField,
    setMiscActivityDate,
    setMiscActivityDescription,
    setMiscActivityHours,
    setMiscActivityMinutes,
    setMiscActivityNotes,
    setMiscActivityStartTime,
    setMiscActivityTab,
    setMiscActivityTypeId,
    setMiscCaseActivityId,
    updateCaseChargeableTime,
    updateCaseEventChargeable,
    setCallNoteCaseActivityId,
    setCallNoteDescription,
    setCallNoteMode,
    setCallNoteText,
    setInternalNoteCaseActivityId,
    setInternalNoteText,
    reassignCaseActivity,
    setAdjustCaseMinutes,
    setCaseActivityDuration,
    setActivityManage,
} from "../redux/actions/caseActions";
import { miscActivityGroups } from "../constants/miscActivityGroupConstants";
import ChargeableIcon from "./ChargeableIcon";
import callNoteModes from "../constants/callNoteModes";
import ReassignActivityDialog from "./dialogs/ReassignActivityDialog";
import { setSnackAction } from "../redux/actions/snackActions";
import AdjustActivityTimeDialog from "./dialogs/AdjustActivityTimeDialog";
import caseActivityService from "../services/caseActivityService";
import queryKeys from "../constants/queryKeys";
import reactQueryClient from "../reactQueryClient";
import { MiscActivityCategory } from "../constants/miscActivityCategory";

const useStyles = makeStyles(() => ({
    loggedText: {
        fontSize: 12,
    },
}));

const { CALL, NOTE, MISC, INTERNAL_NOTE, EMAIL, MEETING, CALL_WITH_NOTE, CHAT, CHAT_WITH_NOTE } = caseActivityTypes;

const CaseActivityMenu = ({
    caseId,
    contextEvent,
    contextAnchorEl,
    caseListOpen,
    handleCloseContextMenu,
    handleCaseListOpen,
    onReply,
    onForward,
    handleEditDescription,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const userState = useSelector((state) => state.userReducer);
    const { cases } = useSelector((state) => state.caseReducer);
    const c = cases[caseId];
    const caseIsChargeable = c.caseSummary.isChargeable;
    const contractId = c?.caseSummary?.contractId;

    // confirmation dialog
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [newCaseId, setNewCaseId] = useState(null);
    const [reassigning, setReassigning] = useState(false);

    // adjust activity time dialog
    const [adjustOpen, setAdjustOpen] = useState(false);
    const [adjustEvent, setAdjustEvent] = useState(null);
    const [adjustMinutes, setAdjustMinutes] = useState(0);
    const [adjustHours, setAdjustHours] = useState(0);
    const [dialogStep, setDialogStep] = useState(0);
    const [adjustSaving, setAdjustSaving] = useState(false);

    const caseList = Object.keys(cases).filter((c) => c !== caseId.toString());

    const handleSetCaseActivityChargeable = async (caseActivityId, activityIsChargeable) => {
        try {
            const { data } = await caseService.makeCaseActivityChargeable({
                caseActivityId,
                isChargeable: !activityIsChargeable,
                isExcluded: contextEvent.isExcluded
            });

            reactQueryClient.invalidateQueries([queryKeys.caseTime, caseId]);
            reactQueryClient.invalidateQueries([queryKeys.caseActivities, caseId, 10]);
            reactQueryClient.invalidateQueries([queryKeys.contractSummary, contractId]);

            dispatch(
                updateCaseEventChargeable({
                    caseId,
                    caseActivityId: data.id,
                    isChargeable: data.isChargeable,
                    isExcluded: data.isExcluded
                })
            );
            dispatch(
                updateCaseChargeableTime({
                    caseId,
                    delta: data.isChargeable ? data.duration : -data.duration,
                })
            );

            handleCloseContextMenu();
        } catch (e) {
            console.error(e);
        }
    };

    const handleSetCaseActivityExcluded = async (caseActivityId, activityIsExcluded) => {
        try {
            const { data } = await caseService.makeCaseActivityChargeable({
                caseActivityId,
                isExcluded: !activityIsExcluded,
                isChargeable: contextEvent.isChargeable
            });

            reactQueryClient.invalidateQueries([queryKeys.caseTime, caseId]);
            reactQueryClient.invalidateQueries([queryKeys.caseActivities, caseId, 10]);
            reactQueryClient.invalidateQueries([queryKeys.contractSummary, contractId]);

            dispatch(
                updateCaseEventChargeable({
                    caseId,
                    caseActivityId: data.id,
                    isChargeable: data.isChargeable,
                    isExcluded: data.isExcluded
                })
            );            

            handleCloseContextMenu();
        } catch (e) {
            console.error(e);
        }
    };

    const handleEditMiscActivity = (a) => {
        handleCloseContextMenu();
        dispatch(setActivityManage(caseId, caseActivityTypes.MISC));
        dispatch(setMiscActivityTypeId(a.miscActivityTypeId, caseId, a.miscActivityGroup));
        dispatch(setMiscActivityDescription(a.activityDescription, caseId, a.miscActivityGroup));
        dispatch(setMiscCaseActivityId(a.caseActivityId, caseId, a.miscActivityGroup));
        dispatch(setExistingMiscActivityFiles(a.attachments, caseId, a.miscActivityGroup));
        dispatch(setMiscActivityNotes(a.content, caseId, a.miscActivityGroup));

        if (a.miscActivityGroup === miscActivityGroups.RETROSPECTIVE || a.miscActivityGroup === miscActivityGroups.DRAFTING) {
            dispatch(setMiscActivityDate(a.eventTime, caseId));
            dispatch(setMiscActivityStartTime(a.eventTime, caseId));
            const minutes = Math.floor((a.duration / 60) % 60);
            const hours = Math.floor((a.duration / (60 * 60)) % 24);
            dispatch(setMiscActivityMinutes(minutes, caseId));
            dispatch(setMiscActivityHours(hours, caseId));
            dispatch(
                setMiscActivityTab({
                    caseId,
                    tab: miscActivityGroups.RETROSPECTIVE,
                })
            );
        } else {
            dispatch(setCurrentMiscActivityOpen({ isOpen: true }));
            dispatch(setMiscActivityTab({ caseId, tab: miscActivityGroups.CURRENT }));
        }       
    };

    const handleEditMeetingActivity = (a) => {
        handleCloseContextMenu();

        dispatch(setMeetingCardMeeting(caseId, {
            subject: a.subject,
            startTime: a.eventTime,            
            meetingId: 'meetingId'
        }));

        let minutes = a.duration / 60;        

        dispatch(setMeetingField(caseId, "description", a.activityDescription));
        dispatch(setMeetingField(caseId, "hours", Math.floor((a.duration / (60 * 60)) % 24)));
        dispatch(setMeetingField(caseId, "minutes", minutes % 60));
        dispatch(setMeetingField(caseId, "caseActivityId", a.caseActivityId));
        dispatch(setMeetingField(caseId, "notes", a.content));        
        dispatch(setActivityManage(caseId, caseActivityTypes.MEETING));        
    };

    const handleReassignActivity = async () => {
        setReassigning(true);
        const response = await caseActivityService.reassignCaseActivity(newCaseId, caseId, contextEvent.caseActivityId, userState.userProfile.userId);

        if (response.status === 200) {
            reactQueryClient.invalidateQueries([queryKeys.caseTime, caseId]);
            reactQueryClient.invalidateQueries([queryKeys.caseActivities, caseId, 10]);
            reactQueryClient.invalidateQueries([queryKeys.contractSummary, contractId]);
            dispatch(reassignCaseActivity(contextEvent.caseActivityId, caseId, newCaseId));
            if (response.data) {
                dispatch(setAdjustCaseMinutes(caseId, (-1 * response.data) / 60));
                dispatch(setAdjustCaseMinutes(newCaseId, response.data / 60));
            }

            dispatch(setSnackAction("Successfully reassigned activity!", "success"));
        } else dispatch(setSnackAction("Something went wrong!", "error"));

        handleCloseContextMenu();
        handleCloseConfirmationDialog();
        setReassigning(false);
    };

    const handleOpenConfirmationDialog = (c) => {
        setNewCaseId(c);
        setConfirmOpen(true);
    };

    const handleCloseConfirmationDialog = () => {
        setNewCaseId(null);
        setConfirmOpen(false);
    };

    const handleClickAdjustEvent = (event) => {
        setAdjustEvent(event);

        const seconds = event.duration;

        setAdjustHours(Math.floor(seconds / 3600));
        setAdjustMinutes(Math.round((seconds % 3600) / 60));

        setAdjustOpen(true);
    };

    const handleCloseAdjustDialog = () => {
        setAdjustOpen(false);
        setDialogStep(0);
        setAdjustMinutes(0);
        setAdjustHours(0);
        setAdjustEvent(null);
    };

    const handleClickConfirmAdjust = async () => {
        if (adjustMinutes < 0 || adjustHours < 0) {
            dispatch(setSnackAction("Adjusted time can't be negative", "error"));
            return;
        }

        if (dialogStep === 0) {
            setDialogStep(1);
            return;
        }

        setAdjustSaving(true);
        const newDurationSeconds = adjustHours * 3600 + adjustMinutes * 60;
        const deltaSeconds = newDurationSeconds - adjustEvent.duration;

        const response = await caseActivityService.adjustCaseActivityDuration(
            adjustEvent.caseActivityId,
            userState.userProfile.userId,
            newDurationSeconds,
            caseId
        );

        if (response.status === 200) {
            reactQueryClient.invalidateQueries([queryKeys.caseTime, caseId]);
            reactQueryClient.invalidateQueries([queryKeys.caseActivities, caseId, 10]);
            reactQueryClient.invalidateQueries([queryKeys.contractSummary, contractId]);
            dispatch(setCaseActivityDuration(newDurationSeconds, caseId, adjustEvent.caseActivityId));
            setAdjustCaseMinutes(caseId, deltaSeconds / 60);

            if (adjustEvent.isChargeable) {
                dispatch(updateCaseChargeableTime({ caseId, delta: deltaSeconds }));
            }

            dispatch(setSnackAction("Successfully adjusted duration", "success"));
        }

        setAdjustSaving(false);
        handleCloseAdjustDialog();
        handleCloseContextMenu();
    };

    const handleEditCallNote = async (note) => {
        handleCloseContextMenu();
        dispatch(
            setCallNoteText(
                {
                    issue: note.issue,
                    advice: note.advice,
                    action: note.action,
                },
                caseId
            )
        );
        dispatch(setCallNoteMode(callNoteModes.NOTE, caseId));
        dispatch(setActivityManage(caseId, caseActivityTypes.CALL)); 
        dispatch(setCallNoteCaseActivityId(note.caseActivityId, caseId));
        dispatch(setCallNoteDescription(note.activityDescription));        
    };

    const handleEditInternalNote = async (note) => {
        handleCloseContextMenu();
        dispatch(
            setInternalNoteDescription({
                description: note.activityDescription,
                caseId,
            })
        );
        dispatch(setInternalNoteText(note.text, caseId));
        dispatch(setActivityManage(caseId, caseActivityTypes.NOTE)); 
        dispatch(setInternalNoteCaseActivityId(note.caseActivityId, caseId));        
    };

    return (
        <>
            <Menu anchorEl={contextAnchorEl} keepMounted open={Boolean(contextAnchorEl)} onClose={handleCloseContextMenu}>
                {contextEvent && (contextEvent.itemType === EMAIL || contextEvent.itemType === CALL || contextEvent.itemType === CHAT) && (
                    <MenuItem onClick={() => handleEditDescription(contextEvent)}>
                        <ListItemIcon>
                            <EditIcon />
                        </ListItemIcon>
                        <ListItemText primary="Edit" />
                    </MenuItem>
                )}
                {contextEvent &&
                    ((contextEvent.itemType === EMAIL && contextEvent.direction === 0) ||
                        contextEvent.miscActivityCategory === MiscActivityCategory.SeekAdvice) && (
                        <MenuItem onClick={() => onReply(contextEvent, false)}>
                            <ListItemIcon>
                                <ReplyIcon />
                            </ListItemIcon>
                            <ListItemText primary="Reply" />
                        </MenuItem>
                    )}
                {contextEvent && contextEvent.itemType === EMAIL && contextEvent.direction === 0 && (
                    <MenuItem onClick={() => onReply(contextEvent, true)}>
                        <ListItemIcon>
                            <ReplyIcon />
                        </ListItemIcon>
                        <ListItemText primary="Reply All" />
                    </MenuItem>
                )}
                {contextEvent && contextEvent.itemType === EMAIL && (
                    <MenuItem onClick={() => onForward(contextEvent)}>
                        <ListItemIcon>
                            <ForwardIcon />
                        </ListItemIcon>
                        <ListItemText primary="Forward" />
                    </MenuItem>
                )}
                {contextEvent && (contextEvent.itemType === NOTE || contextEvent.itemType === CALL_WITH_NOTE || contextEvent.itemType === CHAT_WITH_NOTE) && !contextEvent.isLocked && (
                    <MenuItem onClick={() => handleEditCallNote(contextEvent)}>
                        {" "}
                        <ListItemIcon>
                            <EditIcon />
                        </ListItemIcon>
                        <ListItemText primary="Edit" />
                    </MenuItem>
                )}
                {contextEvent && contextEvent.itemType === INTERNAL_NOTE && !contextEvent.isLocked && (
                    <MenuItem onClick={() => handleEditInternalNote(contextEvent)}>
                        <ListItemIcon>
                            <EditIcon />
                        </ListItemIcon>
                        <ListItemText primary="Edit" />
                    </MenuItem>
                )}
                {contextEvent &&
                    contextEvent.itemType === MISC &&
                    contextEvent.miscActivityCategory !== MiscActivityCategory.SeekAdvice &&
                    !contextEvent.isLocked && (
                        <MenuItem onClick={() => handleEditMiscActivity(contextEvent)}>
                            <ListItemIcon>
                                <EditIcon />
                            </ListItemIcon>
                            <ListItemText primary="Edit" />
                        </MenuItem>
                    )}
                {contextEvent &&
                    contextEvent.itemType === MEETING &&
                    !contextEvent.isLocked && (
                        <MenuItem onClick={() => handleEditMeetingActivity(contextEvent)}>
                            <ListItemIcon>
                                <EditIcon />
                            </ListItemIcon>
                            <ListItemText primary="Edit" />
                        </MenuItem>
                    )}
                {contextEvent &&
                    contextEvent.itemType !== CALL && 
                    contextEvent.itemType !== CHAT && 
                    contextEvent.miscActivityCategory !== MiscActivityCategory.SeekAdvice &&
                    (contextEvent.itemType !== EMAIL || contextEvent.direction !== 0) && (
                        <MenuItem onClick={() => handleClickAdjustEvent(contextEvent)}>
                            <ListItemIcon>
                                <AccessTime />
                            </ListItemIcon>
                            <ListItemText primary="Adjust activity time" />
                        </MenuItem>
                    )}
                <MenuItem onClick={() => handleCaseListOpen(true)} data-cy="reassign_activity">
                    <ListItemIcon>
                        <ReAssignIcon />
                    </ListItemIcon>
                    <ListItemText primary="Reassign activity" />
                </MenuItem>
                {contextEvent?.duration > 0 && !contextEvent?.isExcluded && (
                    <MenuItem onClick={() => handleSetCaseActivityChargeable(contextEvent?.caseActivityId, contextEvent?.isChargeable)}>
                        <ListItemIcon>
                            <ChargeableIcon />
                        </ListItemIcon>
                        <ListItemText primary={`Make Activity ${contextEvent?.isChargeable ? "Not Chargeable" : "Chargeable"}`} />
                    </MenuItem>
                )}
                {contextEvent?.duration > 0 && !contextEvent?.isChargeable && (!caseIsChargeable || contextEvent?.isExcluded) && (
                    <MenuItem onClick={() => handleSetCaseActivityExcluded(contextEvent?.caseActivityId, contextEvent?.isExcluded)}>
                        <ListItemIcon>
                            <ChargeableIcon />
                        </ListItemIcon>
                        <ListItemText primary={`Make Activity ${contextEvent?.isExcluded ? "Included" : "Excluded"}`} />
                    </MenuItem>
                )}
                {caseListOpen && (
                    <Grow in={caseListOpen}>
                        <MenuList className={classes.noPad}>
                            {reassigning ? (
                                <CircularProgress />
                            ) : !caseList.length ? (
                                <MenuItem>Please open a case</MenuItem>
                            ) : (
                                caseList.map((c) => (
                                    <MenuItem onClick={() => handleOpenConfirmationDialog(c)} key={c} data-cy="reassign_case_option">
                                        <ListItemIcon>
                                            <WorkOutline />
                                        </ListItemIcon>
                                        <ListItemText primary={`Case ${c}`} />
                                    </MenuItem>
                                ))
                            )}
                        </MenuList>
                    </Grow>
                )}
                {contextEvent && contextEvent.direction === 0 && contextEvent.adviserId && (
                    <ListSubheader className={classes.loggedText}>Logged by {userState.users[contextEvent.adviserId].name}</ListSubheader>
                )}
            </Menu>
            <ReassignActivityDialog
                confirmOpen={confirmOpen}
                handleConfirmOpen={setConfirmOpen}
                attachmentCount={contextEvent?.attachments?.length}
                newCaseId={newCaseId}
                handleCloseConfirmationDialog={handleCloseConfirmationDialog}
                handleReassignActivity={handleReassignActivity}
            />
            <AdjustActivityTimeDialog
                adjustOpen={adjustOpen}
                adjustHours={adjustHours}
                adjustMinutes={adjustMinutes}
                adjustEvent={adjustEvent}
                adjustSaving={adjustSaving}
                dialogStep={dialogStep}
                handleAdjustHours={setAdjustHours}
                handleAdjustMinutes={setAdjustMinutes}
                handleCloseAdjustDialog={handleCloseAdjustDialog}
                handleClickConfirmAdjust={handleClickConfirmAdjust}
            />
        </>
    );
};

export default CaseActivityMenu;
